import { httpRequest, parseQuery } from '@calendly/http-tools'
import { parseInTimezone, isValidTimezone, now, toDateId, startOfMonth } from '@calendly/datetime'

import {
  schedulingLinkSelector
} from 'selectors/event_type'
import { eventTypeSelector } from 'root-api'
import { isPublisherSelector, timezoneSelector } from 'selectors'
import { isDiagnosticsEnabledSelector } from 'selectors/diagnostics'
import { currentInviteeSelector, isReschedulingSelector } from 'selectors/current_invitee'

const BOOKING_METHODS = {
  instant: 'instant',
  poll: 'poll',
}

const DAYS_IN_BATCH = 7
const DAYS_THRESHOLD = 3

const buildReschedulingParams = (state) => ({
  is_publisher: isPublisherSelector(state),
  new_event_user_uuid: parseQuery(window.location.search).new_event_user_uuid,
  invitee_uuid: currentInviteeSelector(state).uuid
})

const buildPollRevoteParams = () => ({
  invitee_uuid: parseQuery(window.location.search).uuid
})

const buildParams = (state, range, duration) => {
  const schedulingLink = schedulingLinkSelector(state)
  const params = {
    timezone: timezoneSelector(state),
    diagnostics: isDiagnosticsEnabledSelector(state),
    range_start: range[0],
    range_end: range[1],
    embed_domain: parseQuery(window.location.search).embed_domain,
    embed_type: parseQuery(window.location.search).embed_type,
    scheduling_link_uuid: schedulingLink?.uid
  }

  if (duration) params.duration_override = duration

  if (eventTypeSelector(state).booking_method === BOOKING_METHODS.poll) {
    params.rescheduling = buildPollRevoteParams(state)
  }

  if (isReschedulingSelector(state)) params.rescheduling = buildReschedulingParams(state)

  return params
}

const fetchCalendarRange = (eventType, params, pathSegment = 'calendar') => (
  httpRequest({
    url: `/api/booking/event_types/${eventType.uuid}/${pathSegment}/range`,
    type: 'GET',
    params
  })
)

export const fetchRangeData = (state, range, duration) => (
  fetchCalendarRange(eventTypeSelector(state), buildParams(state, range, duration))
)

/** Run async fn for each item sequentially; use when parallel calls are restricted (e.g. Microsoft). */
export const runSequentially = (items, fn) => items.reduce(
  (promise, item) => promise.then((results) => fn(item).then((result) => [...results, result])),
  Promise.resolve([])
)

export const fetchAvailabilityTracings = (state, range, duration) => {
  const params = {
    ...buildParams(state, range, duration),
    diagnostics: true // Always enable diagnostics for tracings
  }
  return fetchCalendarRange(eventTypeSelector(state), params, 'calendar_tracings')
}

export const getMonthGetParam = () => {
  const { month } = parseQuery(window.location.search)
  return month
}

const wrapRangesInStrings = (ranges, monthId, timezone) => {
  const monthDt = parseInTimezone(monthId, timezone)

  return ranges.map((range) => {
    const start = toDateId(monthDt.set({ day: range.start }))
    const end = toDateId(monthDt.set({ day: range.end }))
    return [start, end]
  })
}

const validateInputs = (monthId, timezone) => {
  if (typeof monthId !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(monthId)) throw new Error(`Invalid monthId: ${monthId}`)
  if (!timezone || !isValidTimezone(timezone)) throw new Error(`Invalid timezone: ${timezone}`)

  const monthDt = parseInTimezone(monthId, timezone)
  if (!monthDt?.isValid) throw new Error(`Invalid monthId: ${monthId}`)

  return monthDt
}

export const buildRanges = (monthId, isMultiRange, isFullMonth, timezone) => {
  const monthDt = validateInputs(monthId, timezone)
  const currentTime = now(timezone)
  const fullMonth = isFullMonth || toDateId(startOfMonth(currentTime)) !== monthId
  const periodStartDay = fullMonth ? 1 : Math.min(currentTime.day, monthDt.daysInMonth)
  const periodEndDay = monthDt.daysInMonth
  const totalRanges = Math.ceil((periodEndDay - periodStartDay + 1) / DAYS_IN_BATCH)

  if (!isMultiRange || totalRanges < 1) return wrapRangesInStrings([{ start: periodStartDay, end: periodEndDay }], monthId, timezone)

  const ranges = [...Array(totalRanges)].map((_, index) => ({
    start: periodStartDay + DAYS_IN_BATCH * index,
    end: Math.min(periodEndDay, periodStartDay + DAYS_IN_BATCH * index + DAYS_IN_BATCH - 1)
  }))

  const last = ranges[totalRanges - 1]
  const penultimate = ranges[totalRanges - 2]
  const lastRangeTooShort = totalRanges > 1 && (last.end - last.start) <= DAYS_THRESHOLD
  if (lastRangeTooShort) {
    penultimate.end = last.end
    ranges.pop()
  }

  return wrapRangesInStrings(ranges, monthId, timezone)
}
